import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaCog,
  FaDollarSign,
  FaSave,
  FaTimes,
} from "react-icons/fa";

const ServicesManagement = () => {
  const [activeTab, setActiveTab] = useState("services");
  const [services, setServices] = useState([]);
  const [extraCharges, setExtraCharges] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      let endpoint;
      switch (activeTab) {
        case "services":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/services`;
          break;
        case "charges":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/extra-charges`;
          break;
      }

      const response = await fetch(endpoint, {
        credentials: "include",
      });
      const data = await response.json();

      switch (activeTab) {
        case "services":
          setServices(data.services || []);
          break;
        case "charges":
          setExtraCharges(data.extra_charges || []);
          break;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      let endpoint, method, data;

      if (editingItem) {
        method = "PUT";
        switch (activeTab) {
          case "services":
            endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/services/${editingItem.id}`;
            break;
          case "charges":
            endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/extra-charges/${editingItem.id}`;
            break;
        }
        data = formData;
      } else {
        method = "POST";
        switch (activeTab) {
          case "services":
            endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/services`;
            break;
          case "charges":
            endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/extra-charges`;
            break;
        }
        data = {
          ...formData,
        };
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      });

      if (response.ok) {
        alert(
          `${activeTab.slice(0, -1)} ${
            editingItem ? "updated" : "created"
          } successfully!`
        );
        setFormData({});
        setEditingItem(null);
        setShowModal(false);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error saving:", error);
      alert("Error saving");
    }
    setIsLoading(false);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (itemId) => {
    if (
      !window.confirm(
        `Are you sure you want to delete this ${activeTab.slice(0, -1)}?`
      )
    ) {
      return;
    }

    try {
      let endpoint;
      switch (activeTab) {
        case "services":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/services/${itemId}`;
          break;
        case "charges":
          endpoint = `${process.env.REACT_APP_BASE_URL}/api/assets/extra-charges/${itemId}`;
          break;
      }

      const response = await fetch(endpoint, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        alert(`${activeTab.slice(0, -1)} deleted successfully!`);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      console.error("Error deleting:", error);
      alert("Error deleting");
    }
  };

  const openAddModal = () => {
    setFormData({
      name: "",
      description: "",
      ...(activeTab === "services" && {
        service_type: "receiving",
        base_rate: "",
      }),
      ...(activeTab === "charges" && { amount: "" }),
    });
    setEditingItem(null);
    setShowModal(true);
  };

  const renderFormFields = () => {
    switch (activeTab) {
      case "services":
        return (
          <>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Service Name *
              </label>
              <input
                type="text"
                value={formData.name || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Service Type *
              </label>
              <select
                value={formData.service_type || "receiving"}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    service_type: e.target.value,
                  }))
                }
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
              >
                <option value="receiving">Receiving Service</option>
                <option value="provider">Provider Service</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Base Rate *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.base_rate || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    base_rate: e.target.value,
                  }))
                }
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Description
              </label>
              <textarea
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                rows="3"
              />
            </div>
          </>
        );

      case "charges":
        return (
          <>
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Charge Name *
              </label>
              <input
                type="text"
                value={formData.name || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Amount *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.amount || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, amount: e.target.value }))
                }
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-slate-700">
                Description
              </label>
              <textarea
                value={formData.description || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-full px-4 py-3 border border-slate-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium"
                rows="3"
              />
            </div>
          </>
        );

      default:
        return null;
    }
  };

  const renderDataList = () => {
    let data;
    switch (activeTab) {
      case "services":
        data = services;
        break;
      case "charges":
        data = extraCharges;
        break;
      default:
        data = [];
    }

    if (data.length === 0) {
      return (
        <div className="text-center py-16">
          <div className="p-6 bg-slate-100 rounded-full w-fit mx-auto mb-6">
            <FaCog className="w-12 h-12 text-slate-400" />
          </div>
          <h4 className="text-2xl font-bold text-slate-800 mb-4">
            No {activeTab} Found
          </h4>
          <p className="text-slate-600 max-w-md mx-auto leading-relaxed mb-8">
            You haven't created any {activeTab} yet.
          </p>
          <button
            className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200"
            onClick={openAddModal}
          >
            Create Your First {activeTab.slice(0, -1)}
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.map((item) => (
          <div
            key={item.id}
            className="bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-bold text-slate-800">
                    {item.name}
                  </h4>
                  {item.description && (
                    <p className="text-sm text-slate-600 mt-1">
                      {item.description}
                    </p>
                  )}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                  >
                    <FaEdit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="bg-slate-50/80 rounded-lg p-3 space-y-2">
                {activeTab === "services" && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Type:</span>
                      <span className="font-medium capitalize">
                        {item.service_type}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Base Rate:</span>
                      <span className="font-medium">${item.base_rate}</span>
                    </div>
                  </>
                )}

                {activeTab === "charges" && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Amount:</span>
                    <span className="font-medium">${item.amount}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
          Services & Charges Management
        </h2>
        <p className="mt-3 text-lg text-slate-600 max-w-2xl mx-auto">
          Manage your business services and extra charges
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-lg p-8">
        <div className="flex space-x-1 bg-slate-100 rounded-xl p-1 mb-6">
          <button
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === "services"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
            onClick={() => setActiveTab("services")}
          >
            <FaCog className="w-4 h-4" />
            <span>Services</span>
          </button>
          <button
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === "charges"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-800"
            }`}
            onClick={() => setActiveTab("charges")}
          >
            <FaDollarSign className="w-4 h-4" />
            <span>Extra Charges</span>
          </button>
        </div>

        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-slate-800 capitalize">
            {activeTab}
          </h3>
          <button
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200"
            onClick={openAddModal}
          >
            <FaPlus className="w-4 h-4" />
            <span>Add {activeTab.slice(0, -1)}</span>
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          renderDataList()
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingItem ? "Edit" : "Add"} {activeTab.slice(0, -1)}
                </h3>
                <button
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                  onClick={() => {
                    setShowModal(false);
                    setEditingItem(null);
                    setFormData({});
                  }}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {renderFormFields()}

              <div className="flex space-x-4 pt-4">
                <button
                  type="button"
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 rounded-xl hover:bg-slate-50 font-semibold transition-colors"
                  onClick={() => {
                    setShowModal(false);
                    setEditingItem(null);
                    setFormData({});
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 font-semibold shadow-lg shadow-blue-500/25 transition-all duration-200 disabled:opacity-50"
                >
                  <FaSave className="w-4 h-4" />
                  <span>{editingItem ? "Update" : "Create"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManagement;
